import React from 'react';
import { MetricId } from '../../../../api/types-graphql';
import {
  useEffectiveRoleViewAuthorizationServiceContext,
  useMetricDetailsMapContext,
} from '../../../../context/contexts';
import { MetricIdTypeArrays } from '../../../../types';
import { MetricQueryTooltip } from '../../modals/MetricQueryModal';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  useMetricDisplayValues,
  useMultipleMetricsQuery,
} from '../common/hooks';
import { DataOrError, ExternalChartComponentProps } from '../common/types';
import {
  MultiMetricComparisonMetricValue,
  MultiMetricComparisonVizConfig,
  MultiMetricComparisonVizData,
  MultiMetricComparisonVizDataResponse,
} from './types';

/**
 * Generates the chart title for multi-metric comparison.
 * Uses the config title if provided, otherwise generates a default title.
 * Includes SQL tooltip in extraContent for dev options.
 */
const useChartTitle = (
  config: MultiMetricComparisonVizConfig,
  externalProps: ExternalChartComponentProps,
  metricsSql: (string | null)[]
): TitleConfig => {
  const { locale } = externalProps;
  const titleFromConfig = config.title?.[locale.selected];
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();
  const canSeeChartDevOptions = authorizationService.canSeeChartDevOptions();

  // Build metrics array for the tooltip
  const metrics = config.queries.map((q) => q.metric) as MetricIdTypeArrays;

  const extraContent = canSeeChartDevOptions ? (
    <MetricQueryTooltip metrics={metrics} metricsSql={metricsSql} />
  ) : undefined;

  // If a title is provided in config, use it
  if (titleFromConfig) {
    return {
      mainTitle: titleFromConfig,
      extraContent,
    };
  }

  // Default title - could be enhanced with i18n
  return {
    mainTitle: '',
    extraContent,
  };
};

/**
 * Fetches data for multiple metrics in a single query and transforms it into
 * the response format needed for the visualization.
 */
const useMultiMetricComparisonResponse = (
  config: MultiMetricComparisonVizConfig,
  externalProps: ExternalChartComponentProps,
  metricDisplayValues: Partial<Record<MetricId, string>>
): DataOrError<MultiMetricComparisonVizDataResponse> => {
  const { timeSliderState, segmentationState, filtersState, locale } =
    externalProps;
  const { queries, timeSelection, filters } = config;

  // Collect all metrics into a single array
  const metrics = queries.map((q) => q.metric) as MetricIdTypeArrays;

  const { queryResponse } = useMultipleMetricsQuery({
    metrics,
    timeSelection,
    filters,
    timeSliderState,
    segmentationState,
    filtersState,
  });

  // Check for errors
  if (queryResponse.error) {
    return { data: null, error: queryResponse.error };
  }

  // Transform responses into metric values
  const metricValues: MultiMetricComparisonMetricValue[] = queries.map(
    (queryConfig, index) => {
      const metricResult = queryResponse.data[index];

      // Extract the aggregate value from the response
      // When there's no segmentation, there should be a single group segment with the total
      const value =
        (metricResult.segments[0]?.groupSegments[0]?.data as number) ?? 0;

      // Determine the label - use custom label if provided, otherwise use metric display name
      const customLabel = queryConfig.label?.[locale.selected];
      const metricDisplayName =
        metricDisplayValues[queryConfig.metric.value as unknown as MetricId] ??
        String(queryConfig.metric.value);
      const label = customLabel ?? metricDisplayName;

      return {
        metric: queryConfig.metric,
        label,
        value,
        metricSql: metricResult.meta.metricSql,
      };
    }
  );

  return {
    data: { metricValues },
    error: null,
  };
};

/**
 * Hook to convert MultiMetricComparisonVizConfig to data for visualization.
 * Fetches multiple metrics in parallel and returns formatted data.
 */
export const useMultiMetricComparisonConfigToData = (
  config: MultiMetricComparisonVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<MultiMetricComparisonVizData> => {
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricDisplayValues = useMetricDisplayValues(
    config.queries.map((q) => q.metric.value as unknown as MetricId)
  );

  const { data, error } = useMultiMetricComparisonResponse(
    config,
    externalProps,
    metricDisplayValues
  );

  const metricsSql = data?.metricValues.map((v) => v.metricSql) ?? [];
  const titleConfig = useChartTitle(config, externalProps, metricsSql);

  if (error) {
    return { data: null, error };
  }

  return {
    data: {
      dataResponse: data,
      titleConfig,
      metricDetails: metricDetailsMap,
      metricDisplayValues,
      chartColors: config.chartColors,
      isHorizontal: config.isHorizontal,
    },
    error: null,
  };
};
